import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { constants } from "@qgiv/core-js";
import { createEntityReplaceForRegistration } from "@qgiv/p2p-registration";
import { selectCmsControlByIdByPage } from "../../../redux/slices/cmsSettingsSlice";
import { selectAllP2pSettings } from "../../../redux/slices/p2pSettingsSlice";
import { selectHasParentRegistration } from "../../../redux/slices/appSettingsSlice";
import { selectParentRegistration } from "../../../redux/slices/parentRegistrationSlice";
import { selectRegistrationsFromRegistration } from "../../../redux/slices/registrationSlice";
import { selectAllFormSettings } from "../../../redux/slices/formSettingsSlice";
import { selectTeams } from "../../../redux/slices/teamsSlice";
import StorePurchaseRecipient from "./StorePurchaseRecipient";

const ConnectedStorePurchaseRecipient = ({ controlId }) => {
    const FUNDRAISING_RECIPIENT_PLACEHOLDER = "%FundraisingRecipient%";

    const {
        ENUMS: { DefaultView },
    } = constants;

    const control = useSelector((state) =>
        selectCmsControlByIdByPage(
            controlId,
            DefaultView.STANDARD_P2P_STORE,
            state,
        ),
    );
    const {
        options: {
            creditedStorePurchaseText = `Purchase applies to the fundraising goal for: ${FUNDRAISING_RECIPIENT_PLACEHOLDER}`,
        },
    } = control;

    const {
        includePurchasesInGoal,
        enablePurchaseToParticipant,
        enablePurchaseToTeam,
        enablePurchaseToEvent,
    } = useSelector(selectAllP2pSettings);

    const hasParentRegistration = useSelector(selectHasParentRegistration);
    const parentRegistration = useSelector(selectParentRegistration);
    const registrations = useSelector(selectRegistrationsFromRegistration);
    const { title } = useSelector(selectAllFormSettings);
    const teams = useSelector(selectTeams);

    const createPurchaseEntityReplace = () => {
        const PARTICIPANT_DEFAULT = "Participant";

        const isNfp = !!(
            registrations.length > 0 &&
            registrations[0].Is_Nfp &&
            registrations[0].Is_Nfp === true
        );

        // if it is not to be credited to the participant
        if (isNfp || !enablePurchaseToParticipant) {
            // reuse the created entity logic from donations
            return createEntityReplaceForRegistration(
                registrations,
                teams,
                undefined,
                undefined,
                enablePurchaseToTeam,
                enablePurchaseToEvent,
                false,
                title,
            );
        }

        // this should be credited to the participant
        // use the parent if we have one
        if (hasParentRegistration) {
            return parentRegistration.First_Name
                ? `${parentRegistration.First_Name} ${parentRegistration.Last_Name}`
                : PARTICIPANT_DEFAULT;
        }

        // use the participant's name
        if (registrations[0].First_Name) {
            return `${registrations[0].First_Name} ${registrations[0].Last_Name}`;
        }

        // fallback to default participant text
        return PARTICIPANT_DEFAULT;
    };

    const entityReplace = createPurchaseEntityReplace(
        enablePurchaseToParticipant,
        enablePurchaseToTeam,
        enablePurchaseToEvent,
    );

    // connected properties
    const displayPurchaseRecipient = includePurchasesInGoal;
    const creditedStorePurchaseTextWithRecipient =
        creditedStorePurchaseText.replace(
            FUNDRAISING_RECIPIENT_PLACEHOLDER,
            entityReplace,
        );

    const storePurchaseRecipientProps = {
        displayPurchaseRecipient,
        creditedStorePurchaseTextWithRecipient,
    };
    return <StorePurchaseRecipient {...storePurchaseRecipientProps} />;
};

ConnectedStorePurchaseRecipient.propTypes = {
    controlId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
        .isRequired,
};

export default ConnectedStorePurchaseRecipient;
